import type { SplitRule } from "@/api/schemas";

/**
 * Trieda hrúbky dosky a pravidlá lepenia (KOLO 188).
 *
 * Zrkadlo `backend/src/tandem_budget/services/trieda_hrubky.py` — tá istá
 * tabuľka prípadov je v `splitRules.test.ts` aj `tests/test_trieda_hrubky.py`.
 *
 * - **Trieda** dosky = hrúbka nadol na celé mm (18,0–18,9 → 18). Pravidlá
 *   lepenia sú v triedach, doska v cenníku nesie skutočnú hrúbku (18,7).
 * - **Lepený dielec má skutočnú hrúbku** — súčet vrstiev (2 × 18,7 = 37,4).
 *   Pravidlo k nemu patrí podľa okna `hrubka ≤ z < hrubka + n` (n = najviac
 *   vrstiev pravidla); celá hrúbka (36, 29) presne.
 */

/** Hrúbka bez šumu desatinných čísel (18,7 + 18,7 = 37,4, nie 37,39999…). */
function zaokruhli(z: number): number {
  return Math.round(z * 1000) / 1000;
}

/** Trieda dosky: hrúbka nadol na celé mm (18,7 → 18, 18 → 18). */
export function trieda(z: number): number {
  return Math.floor(zaokruhli(z));
}

function maxVrstiev(rule: SplitRule): number {
  return Math.max(0, ...rule.kombinacie.map((c) => c.length));
}

/**
 * Pravidlo, podľa ktorého sa dielec hrúbky `z` lepí, alebo null.
 *
 * Celá hrúbka presne (36 → 36, 29 → nič); desatinná v okne — pri viacerých
 * vyhrá najväčšia (37,4 → 36; 36,5 → 36, nie 35 = 25 + 10).
 */
export function pravidloPreHrubku(
  rules: SplitRule[],
  zMm: string | number | null | undefined,
): SplitRule | null {
  const z = zaokruhli(typeof zMm === "number" ? zMm : parseFloat(zMm ?? ""));
  if (!Number.isFinite(z)) return null;
  if (z === trieda(z)) return rules.find((r) => r.hrubka_mm === z) ?? null;
  let najlepsie: SplitRule | null = null;
  for (const r of rules) {
    if (r.hrubka_mm <= z && z < r.hrubka_mm + maxVrstiev(r)) {
      if (najlepsie == null || r.hrubka_mm > najlepsie.hrubka_mm) najlepsie = r;
    }
  }
  return najlepsie;
}

/** Serializuje kombináciu na string "18+18" pre dropdown value. */
export function comboKey(combo: number[]): string {
  return combo.join("+");
}

/**
 * Kombinácie pravidla, v ktorých je materiál aspoň jednou vrstvou — podľa
 * triedy (z 18,7 aj 36 = 18 + 18, z 16 nie). KOLO 162 + 188.
 */
export function kombinacieZMaterialu(rule: SplitRule, matZ: number): number[][] {
  const t = trieda(matZ);
  return rule.kombinacie.filter((combo) => combo.includes(t));
}

/** Prvá kombinácia pravidla, ktorá obsahuje triedu materiálu. */
export function kombinaciaZMaterialu(rule: SplitRule, matZ: number): number[] | null {
  return kombinacieZMaterialu(rule, matZ)[0] ?? null;
}

/**
 * Hrúbka lepeného dielca zo skutočných hrúbok vrstiev.
 *
 * Súčet tried vrstiev = hrúbka pravidla → súčet skutočných (18,7 + 18,7 =
 * 37,4); väčší → frézuje sa na hrúbku pravidla (35 = 18 + 18); menší →
 * vrstvy pravidlu nezodpovedajú, null.
 */
export function hrubkaLepeneho(zVrstiev: number[], hrubkaPravidla: number): number | null {
  if (zVrstiev.length === 0) return null;
  const triedy = zVrstiev.reduce((s, z) => s + trieda(z), 0);
  if (triedy > hrubkaPravidla) return hrubkaPravidla;
  if (triedy < hrubkaPravidla) return null;
  return zaokruhli(zVrstiev.reduce((s, z) => s + z, 0));
}

/**
 * Skutočné hrúbky vrstiev kombinácie, keď sú z dosky `matZ`: vrstva
 * v triede materiálu má jeho hrúbku, ostatné svoju triedu (18 + 10 z 18,7
 * → 18,7 + 10).
 */
export function vrstvyZMaterialu(combo: number[], matZ: number): number[] {
  const t = trieda(matZ);
  return combo.map((v) => (v === t ? zaokruhli(matZ) : v));
}

/** Hrúbka v mm s desatinnou čiarkou (37,4). */
export function formatMm(z: number): string {
  return String(zaokruhli(z)).replace(".", ",");
}
